type EventCallback<T = unknown> = (data: T) => void;

export interface GameEvents {
  'player:damaged': { damage: number; source: string };
  'player:died': { position: { x: number; y: number; z: number } };
  'player:healed': { amount: number };
  'player:ammo': { amount: number };
  'weapon:fired': {
    weaponType: string;
    ammoLeft: number;
    muzzlePosition?: { x: number; y: number; z: number };
    direction?: { x: number; y: number; z: number };
  };
  'weapon:reloading': { weaponType: string; reloadTime: number };
  'weapon:reloaded': { weaponType: string };
  'weapon:switched': { weaponType: string; ammo: number; maxAmmo: number };
  'weapon:added': { weaponType: string; totalWeapons: number };
  'projectile:hit': {
    position: { x: number; y: number; z: number };
    targetType: string;
    normal?: { x: number; y: number; z: number };
  };
  'target:destroyed': { id: string; position: { x: number; y: number; z: number }; points: number; isBoss?: boolean };
  'target:damaged': { id: string; damage: number; healthRemaining: number; isBoss?: boolean };
  'boss:phaseChange': { id: string; phase: number };
  'boss:defeated': { id: string };
  'boss:droneSpawned': { drone: unknown }; // FlyingDrone reference
  'bike:landed': { airTime: number; velocity: number; position: { x: number; y: number; z: number } };
  'bike:launched': { speed: number; launchVelocity: number };
  'score:added': { points: number; reason: string };
  'collectible:picked': { type: string; position: { x: number; y: number; z: number }; value: number };
  // Upgrade events
  'upgrade:buff': { type: 'damage' | 'firerate' | 'speed' | 'shield' };
  'upgrade:permanent': { type: 'fireRateLevel' | 'magazineSizeLevel' | 'projectileSpeedLevel' };
  'upgrade:weapon': { weapon: 'railgun' | 'plasmaCannon' | 'homingMissiles' };
  'upgrade:maxed': { type: string };
  'upgrade:applied': { type: string; newLevel: number; maxLevel: number };
  'upgrade:duplicate': { weapon: string; bonusPoints: number };
  // Buff events
  'buff:activated': { type: string; duration: number; color: number };
  'buff:refreshed': { type: string; duration: number; color: number };
  'buff:expired': { type: string };
  // Weapon unlock events
  'weapon:unlocked': { weapon: string };
}

class EventBusClass {
  private listeners: Map<string, Set<EventCallback>> = new Map();

  public on<K extends keyof GameEvents>(
    event: K,
    callback: EventCallback<GameEvents[K]>
  ): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback as EventCallback);

    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  public off<K extends keyof GameEvents>(
    event: K,
    callback: EventCallback<GameEvents[K]>
  ): void {
    this.listeners.get(event)?.delete(callback as EventCallback);
  }

  public emit<K extends keyof GameEvents>(event: K, data: GameEvents[K]): void {
    this.listeners.get(event)?.forEach((callback) => callback(data));
  }

  public clear(): void {
    this.listeners.clear();
  }
}

// Singleton instance
export const EventBus = new EventBusClass();
